// src/pages/Reports/IssuancePlanReport/index.tsx
// Mục 14.b, README_UPDATE.md — tách khỏi Reports/index.tsx (trước đây gộp 3 báo cáo vào 1 trang có
// tab), giờ mỗi báo cáo là 1 trang/route riêng. Mục 15.b/15.d — thêm STT, phân trang, và xem chi
// tiết (liệt kê các lần giao vé cho đại lý của kỳ này). Dữ liệu lấy trực tiếp từ ticket-service
// (ReportsController) — chỉ đọc, không có thao tác tạo/sửa/xoá nên không cần Redux slice riêng.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Col, Container, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';

import BreadCrumb from 'Components/Common/BreadCrumb';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { getIssuancePlanReportApi, getIssuancePlans } from 'helpers/fakebackend_helper';
import { AgentMap, buildAgentMap, getAgentCode, getAgentName, loadAgentsWithCache } from 'helpers/agentLookup';
import { IAgent } from 'slices/agentManagement/types';
import { IssuancePlan } from 'pages/IssuancePlan/types';
import { useBrandingTitle } from '../../../hooks';
import { IssuancePlanReportRow, ReportPagination } from '../types';
import TicketDeliveryDetailModal from '../components/TicketDeliveryDetailModal';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_plan_report');

const PAGE_SIZE = 10;

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '0';

const formatCurrency = (value?: number | null) =>
  typeof value === 'number' ? st('value_d_text', { value: value.toLocaleString('vi-VN') }) : '-';

const getPayloadItems = (response: any): any[] => {
  const payload = response?.data || response;
  return payload?.items || payload?.data || payload || [];
};

// Loại bỏ param rỗng/undefined trước khi gọi API — xem README_RULE.md mục 1.
const cleanParams = (params: any): any => {
  const cleaned: any = {};
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

const IssuancePlanReport: React.FC = () => {
  useBrandingTitle(st('bao_cao_ky_phat_hanh_text'));

  const [plans, setPlans] = useState<IssuancePlan[]>([]);
  const [agents, setAgents] = useState<IAgent[]>([]);
  const agentMap: AgentMap = useMemo(() => buildAgentMap(agents), [agents]);

  const [planId, setPlanId] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<IssuancePlanReportRow[]>([]);
  const [pagination, setPagination] = useState<ReportPagination>({
    currentPage: 1,
    pageSize: PAGE_SIZE,
    totalItems: 0,
    totalPages: 0,
  });
  const [detailTarget, setDetailTarget] = useState<IssuancePlanReportRow | null>(null);

  useEffect(() => {
    getIssuancePlans({ pageIndex: 1, pageSize: 200 })
      .then((res) => setPlans(getPayloadItems(res) as IssuancePlan[]))
      .catch(() => toast.error(st('khong_the_tai_danh_sach_ke_message')));
    loadAgentsWithCache().then(setAgents);
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getIssuancePlanReportApi(
        cleanParams({
          PlanId: planId || undefined,
          PageIndex: pagination.currentPage,
          PageSize: pagination.pageSize,
        }),
      );
      const payload = (res as any)?.data ?? res;
      setReport(payload?.items || []);
      setPagination((prev) => ({
        ...prev,
        totalItems: payload?.totalItems || 0,
        totalPages: payload?.totalPages || 0,
      }));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_bao_cao_message'));
    } finally {
      setLoading(false);
    }
  }, [planId, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('bao_cao_ky_phat_hanh_text')} pageTitle={st('bao_cao_text')} />

          <Card>
            <CardHeader className="card-header border-0">
              <h5 className="card-title mb-0">{st('bao_cao_ky_phat_hanh_text')}</h5>
            </CardHeader>
            <CardBody>
              <Row className="g-3 mb-4 align-items-end">
                <Col md={4}>
                  <Label className="form-label">{st('ke_hoach_phat_hanh_label')}</Label>
                  <Input
                    type="select"
                    className="form-select"
                    value={planId}
                    onChange={(event) => {
                      setPlanId(event.target.value);
                      setPagination((prev) => ({ ...prev, currentPage: 1 }));
                    }}
                  >
                    <option value="">{st('tat_ca_ke_hoach_option')}</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.planCode}
                      </option>
                    ))}
                  </Input>
                </Col>
              </Row>

              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : (
                <>
                  <DataTableShell
                    headerRow={
                      <tr>
                        <th className="text-center" style={{ width: 50 }}>{st('stt_column')}</th>
                        <th>{st('ma_ky_column')}</th>
                        <th>{st('ngay_mo_thuong_du_kien_column')}</th>
                        <th>{st('trang_thai_column')}</th>
                        <th className="text-end">{st('so_ve_du_kien_in_column')}</th>
                        <th className="text-end">{st('so_ve_thuc_nhan_column')}</th>
                        <th className="text-end">{st('so_ve_da_giao_column')}</th>
                        <th className="text-end">{st('so_ve_da_thu_hoi_column')}</th>
                        <th className="text-end">{st('so_ve_ban_rong_column')}</th>
                        <th className="text-end">{st('doanh_thu_uoc_tinh_column')}</th>
                        <th style={{ width: 110 }} className="text-center">
                          {st('thao_tac_column')}
                        </th>
                      </tr>
                    }
                  >
                    {report.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="text-center text-muted py-5">
                          {st('khong_co_du_lieu_text')}
                        </td>
                      </tr>
                    ) : (
                      report.map((row, index) => (
                        <tr key={row.issuancePlanId}>
                          <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
                          <td className="fw-medium text-primary">{row.planCode}</td>
                          <td>{new Date(row.expectedDrawDate).toLocaleDateString('vi-VN')}</td>
                          <td>{row.statusName || row.status}</td>
                          <td className="text-end">{formatNumber(row.expectedPrintQuantity)}</td>
                          <td className="text-end">{formatNumber(row.actualReceivedQuantity)}</td>
                          <td className="text-end">{formatNumber(row.deliveredQuantity)}</td>
                          <td className="text-end">{formatNumber(row.recalledQuantity)}</td>
                          <td className="text-end">{formatNumber(row.netSoldQuantity)}</td>
                          <td className="text-end">{formatCurrency(row.revenue)}</td>
                          <td className="text-center">
                            <Button
                              color="info"
                              outline
                              size="sm"
                              className="btn-icon"
                              title={st('xem_chi_tiet_cac_lan_giao_title')}
                              onClick={() => setDetailTarget(row)}
                            >
                              <i className="ri-eye-line"></i>
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </DataTableShell>

                  <AgentPagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalItems}
                    pageSize={pagination.pageSize}
                    onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
                  />
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      <TicketDeliveryDetailModal
        isOpen={!!detailTarget}
        title={st('cac_lan_giao_ve_value_text', { value: detailTarget?.planCode || '' })}
        planId={detailTarget?.issuancePlanId}
        showAgentColumn
        getAgentLabel={(id) => `${getAgentName(agentMap, id)} (${getAgentCode(agentMap, id)})`}
        onClose={() => setDetailTarget(null)}
      />
    </React.Fragment>
  );
};

export default IssuancePlanReport;
